<template>
    <new-student-backend-focus-layout
        :page-title="quizTitle"
        exit-button-text="Return to dashboard"
        v-on:exit-page-clicked="goToQuizDashboard"
        header-bg-color="white"
        content-bg-color="gray-125"
    >
        <template #page_header>
            <div class="mx-auto flex flex-col">
                <div class="my-8 text-[26px] font-bold">Quiz Results</div>
                <div class="flex items-center gap-8">
                    <div class="">
                        <new-progress-ring
                            height="144px"
                            :percent-filled="Math.round(quiz.score * 100)"
                        >
                            <div class="text-[14px] text-gray-600">Score</div>
                            <div
                                class="text-[24px] font-semibold text-gray-800"
                            >
                                {{ Math.round(quiz.score * 100) }}%
                            </div>
                        </new-progress-ring>
                    </div>
                    <div class="leading-tight">
                        <div class="flex flex-col gap-4">
                            <div class="">
                                <div class="text-[14px] text-gray-600">
                                    Time Spent
                                </div>
                                <div
                                    class="text-[20px] font-medium text-gray-800"
                                >
                                    <media-duration
                                        format="full"
                                        :duration-in-seconds="
                                            quiz.meta.time_spent
                                        "
                                        :show-seconds="true"
                                    />
                                </div>
                            </div>
                            <div class="">
                                <div class="text-[14px] text-gray-600">
                                    Score
                                </div>
                                <div
                                    class="text-[20px] font-medium text-gray-800"
                                >
                                    {{
                                        Math.round(
                                            quiz.score * quiz.meta.length
                                        )
                                    }}
                                    /
                                    {{ quiz.meta.length }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="my-8">
                    <table class="w-full">
                        <thead>
                            <th
                                class="p-2 pl-4 text-left text-[14px] font-semibold text-gray-700"
                            >
                                Category
                            </th>
                            <th
                                class="w-20 p-2 text-left text-[14px] font-semibold text-gray-700"
                            >
                                Score
                            </th>
                            <th
                                class="hidden p-2 text-left text-[14px] font-semibold text-gray-700 md:table-cell"
                            ></th>
                            <th
                                class="p-2 pr-4 text-right text-[14px] font-semibold text-gray-700"
                            ></th>
                        </thead>
                        <tbody class="text-[16px]">
                            <tr
                                v-for="category in categoryBreakdown"
                                class="border-t border-t-gray-300 odd:bg-gray-50"
                            >
                                <td class="p-2 pl-4">
                                    <text-clamp
                                        :text="category.title"
                                        :max-lines="1"
                                        autoResize
                                    />
                                </td>
                                <td class="p-2">
                                    <student-score-box
                                        :percent-average="category.score"
                                    />
                                </td>
                                <td
                                    class="hidden px-4 py-2 text-gray-600 md:table-cell"
                                >
                                    {{ category.correct }}/{{ category.total }}
                                    Correct
                                </td>
                                <td class="p-2 pr-4 text-right">
                                    <Link
                                        class="text-primary-525 hover:cursor-pointer hover:text-primary-600"
                                        :href="
                                            route('student.focus-area.index', {
                                                focus_area:
                                                    category.focus_area_id,
                                                category_id: category.id,
                                            })
                                        "
                                    >
                                        Review
                                        <span class="hidden md:inline-block"
                                            >Materials</span
                                        >
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
        <template #content>
            <div class="relative mx-auto flex">
                <div class="flex flex-col gap-4">
                    <div class="">
                        <div
                            class="flex items-center justify-center gap-4 py-4"
                        >
                            <div
                                class="flex flex-row justify-center gap-4 overflow-x-scroll whitespace-nowrap px-2 text-[16px] lg:gap-2 lg:border-0 lg:px-0 lg:px-4"
                            >
                                <template
                                    v-for="questionResultStatus in questionResultStatuses"
                                >
                                    <div
                                        v-if="
                                            selectedQuestionResultStatus ===
                                            questionResultStatus
                                        "
                                        class="rounded-full border border-primary-525 bg-primary-525 px-2 py-1 text-[16px] text-white hover:cursor-pointer lg:px-4"
                                    >
                                        {{ questionResultStatus }} ({{
                                            getQuestionStatusCount(
                                                questionResultStatus
                                            )
                                        }})
                                    </div>

                                    <div
                                        v-else
                                        @click="
                                            selectQuestionResultStatus(
                                                questionResultStatus
                                            )
                                        "
                                        class="rounded-full border border-gray-600 px-2 py-1 text-[16px] text-gray-600 hover:cursor-pointer lg:px-4"
                                    >
                                        {{ questionResultStatus }} ({{
                                            getQuestionStatusCount(
                                                questionResultStatus
                                            )
                                        }})
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                    <template v-for="(question, index) in questions">
                        <StudentQuizReviewQuestion
                            v-if="
                                selectedQuestionResultStatus === 'All' ||
                                (selectedQuestionResultStatus === 'Correct' &&
                                    question.student_response.c) ||
                                (selectedQuestionResultStatus === 'Missed' &&
                                    !question.student_response.c)
                            "
                            :quiz-type="quiz.type"
                            :question="question"
                            :question-number="index + 1"
                        />
                    </template>
                </div>

                <div
                    class="absolute -right-16 bottom-0 hidden text-gray-600 hover:cursor-pointer hover:text-gray-925 md:block"
                    @click="scrollToTop()"
                >
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M24 29.8337V18.167"
                            stroke="currentColor"
                            stroke-width="1.66667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M18.167 24.0003L24.0003 18.167L29.8337 24.0003"
                            stroke="currentColor"
                            stroke-width="1.66667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <circle
                            cx="24"
                            cy="24"
                            r="23.5"
                            stroke="currentColor"
                        />
                    </svg>
                </div>
            </div>
        </template>
    </new-student-backend-focus-layout>
</template>

<script>
import NewStudentBackendFocusLayout from "@/_Layouts/NewStudentBackendFocusLayout.vue";
import StudentBackendFocusLayout from "@/_Layouts/NewStudentBackendFocusLayout.vue";
import NewProgressRing from "@/Components/NewProgressRing.vue";
import MediaDuration from "@/Components/MediaDuration.vue";
import StudentScoreBox from "@/Components/StudentScoreBox.vue";
import StudentQuizReviewQuestion from "@/Pages/NewStudentBackend/StudentQuizReviewQuestion.vue";

export default {
    name: "StudentQuizResults",
    components: {
        StudentBackendFocusLayout,
        StudentQuizReviewQuestion,
        StudentScoreBox,
        MediaDuration,
        NewProgressRing,
        NewStudentBackendFocusLayout,
    },
    props: {
        quiz: {
            type: Object,
            default: {},
        },
        quizTitle: {
            type: String,
            default: {},
        },
        questions: {
            type: Array,
            default: [],
        },
        focusArea: {
            type: Object,
            default: {},
        },
        categoryBreakdown: {
            type: Object,
            default: [],
        },
    },
    data() {
        return {
            questionResultStatuses: ["All", "Correct", "Missed"],
            selectedQuestionResultStatus: "All",
        };
    },
    methods: {
        scrollToTop: function () {
            window.scrollTo(0, 0);
        },
        goToQuizDashboard: function () {
            this.$inertia.get(route("student.quizzes.index"));
        },
        selectQuestionResultStatus: function (status) {
            this.selectedQuestionResultStatus = status;
        },
        getQuestionStatusCount(status) {
            let count = 0;

            if (status === "All") {
                return this.questions.length;
            }

            if (status === "Correct") {
                return _.filter(this.questions, function (question) {
                    return question.student_response.c;
                }).length;
            }

            if (status === "Missed") {
                return _.filter(this.questions, function (question) {
                    return !question.student_response.c;
                }).length;
            }
        },
    },
};
</script>
